import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Form, Input, message, Modal, Tabs, Upload } from 'antd'
import RichEditor from '../../../components/RichEditor/RichEditor'
import { FileTextOutlined, UploadOutlined } from '@ant-design/icons'
import ProposalAdvanceStepApprove from './ProposalAdvanceStepApprove'
import { inject, observer } from 'mobx-react'
import SelectFollower from './SelectFollower'
import { ASSIGNEE_TYPE, PROPOSAL_STATUS } from '../../../constants'
import validator from '../../../validator'
import DraggerUploadButton from '../../../components/Common/DraggerUploadButton'
import ProposalStepApproveV2 from '../../Administrative/Proposal/CreateProposalPageV2/ProposalStepApproveV2'

const currentStep = 0
const ProposalAdvanceCreateModal = props => {
  const {
    isModalVisible,
    handleCloseModal,
    proposalAdvanceStore,
    selectPeopleStore,
    proposalStore,
    fileStore,
    authenticationStore,
  } = props

  const {
    defaultApprovalConfigUpdate,
    defaultApprovalConfig,
  } = proposalAdvanceStore
  const { selectUserData, selectDepartmentData } = selectPeopleStore
  const { currentUser } = authenticationStore

  const EDITOR_REF = useRef()
  const [form] = Form.useForm()

  const [isCreating, setIsCreating] = useState(false)
  const [editorContent, setEditorContent] = useState(null)
  const [fileList, setFileList] = useState([])

  useEffect(() => {
    ;(async () => {
      try {
        await proposalAdvanceStore.getDefaultApprovalConfig()
      } catch (err) {
        console.log(err)
        message.error(err.vi || 'Đã có lỗi xảy ra!')
      } finally {
      }
    })()
  }, [])

  const onFinish = async value => {
    const userNextStep = defaultApprovalConfigUpdate.approvalLevels.find(
      item => item.level === currentStep + 1
    )
    if (!userNextStep?.username) {
      return message.warning(`Hãy chọn ${userNextStep.name}!`)
    }
    const batchUploadArr = []
    fileList.forEach(file => {
      const formData = new FormData()
      formData.append('file', file.originFileObj)
      batchUploadArr.push(fileStore.uploadFile(formData))
    })
    try {
      setIsCreating(true)
      const response = await Promise.all(batchUploadArr)
      const payload = {
        title: value.reason,
        content: EDITOR_REF?.current?.editor?.getData(),
        followers: [
          ...selectUserData[ASSIGNEE_TYPE.FOLLOWER],
          ...selectDepartmentData[ASSIGNEE_TYPE.FOLLOWER],
        ].map(item => item.id),
        attachments: response.map(response => response.data.file_id),
        approval_levels: defaultApprovalConfigUpdate.approvalLevels.map(
          item => ({
            ...item,
            status:
              item.level === currentStep + 1
                ? PROPOSAL_STATUS.PENDING
                : item.status,
            user: undefined,
          })
        ),
      }
      await proposalAdvanceStore.createProposalAdvance(payload)
      await proposalAdvanceStore.getProposalAdvanceList()
      handleCancel()
      message.success('Tạo trình tạm ứng thành công!')
    } catch (err) {
      console.log(err)
      message.error(err.vi || 'Đã có lỗi xảy ra!')
    } finally {
      setIsCreating(false)
    }
  }

  const handleChangeFile = useCallback(
    info => {
      const filteredFileList = info.fileList.filter(
        (elem, index, fileArray) =>
          fileArray.findIndex(file => file.name === elem.name) === index
      )
      setFileList(filteredFileList)
    },
    [fileList]
  )

  const handleBeforeUpload = () => false

  const handleCancel = () => {
    handleCloseModal()
    setTimeout(() => {
      selectPeopleStore.clearSelected()
      setFileList([])
      form.resetFields()
      setEditorContent(null)
    }, 100)
  }

  const [approvalLevels, setApprovalLevels] = useState([])

  useEffect(() => {
    const approvalLevelList = [...defaultApprovalConfig.approvalLevels]
    if (approvalLevelList.length === 0) return
    approvalLevelList[0].username = currentUser?.username
    setApprovalLevels(approvalLevelList)
  }, [defaultApprovalConfig, currentUser])

  return (
    <Modal
      wrapClassName="custom-modal-wrapper"
      width="100dvw"
      style={{ top: 30 }}
      title={'Tạo trình tạm ứng'}
      visible={isModalVisible}
      maskClosable={false}
      onCancel={handleCancel}
      footer={null}
      forceRender>
      <Form
        scrollToFirstError={true}
        form={form}
        name={'phieu-trinh-khac-form'}
        layout={'vertical'}
        onFinish={onFinish}>
        <div
          style={{
            display: 'flex',
            height: 'calc(100dvh - 250px)',
            overflowY: 'auto',
            paddingRight: 4,
            gap: 24,
          }}>
          <div style={{ flex: 1, minWidth: 0, height: 'fit-content' }}>
            <h3 style={{ marginBottom: 16, fontSize: 18, fontWeight: 600 }}>
              Thông tin chung
            </h3>

            <Form.Item
              name={'reason'}
              label={'Tiêu đề:'}
              rules={[
                { required: true, message: 'Vui lòng nhập tiêu đề' },
                { max: 500, message: 'Tiêu đề không được dài quá 500 kí tự!' },
                { validator: validator.validateInputString },
              ]}>
              <Input className="h-40" placeholder={'Nhập tiêu đề'} />
            </Form.Item>

            <SelectFollower />

            <Tabs destroyInactiveTabPane={false} defaultActiveKey="1">
              <Tabs.TabPane tab="Nội dung" key="1" forceRender>
                <Form.Item label={'Nội dung:'}>
                  <RichEditor
                    EDITOR_REF={EDITOR_REF}
                    placeholder={'Nhập nội dung'}
                    editorContent={editorContent}
                  />
                </Form.Item>
              </Tabs.TabPane>

              <Tabs.TabPane
                tab="Tài liệu đính kèm"
                key="2"
                forceRender
                style={{ marginTop: 12 }}>
                <Form.Item name={'file_ids'} label={'Tài liệu đính kèm:'}>
                  <Upload.Dragger
                    valuePropName={'fileList'}
                    fileList={fileList}
                    onChange={handleChangeFile}
                    beforeUpload={handleBeforeUpload}
                    multiple={true}
                    className="custom-dragger-upload bg-white">
                    <DraggerUploadButton />
                  </Upload.Dragger>
                </Form.Item>
              </Tabs.TabPane>
            </Tabs>
          </div>
          <div
            style={{
              width: 372,
              flexShrink: 0,
              height: 'fit-content',
            }}>
            <ProposalStepApproveV2
              approvalLevelList={approvalLevels}
              isCheckedFirstStep
              isEditMode={false}
            />
          </div>
        </div>

        {/* <div style={{ marginBottom: 15, marginTop: 10 }}>Chọn người duyệt:</div>
        <ProposalAdvanceStepApprove approvalLevelList={approvalLevels} /> */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
          <Button onClick={handleCancel} danger className="custom-ant-button">
            Đóng
          </Button>
          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={isCreating}
            className="custom-ant-button">
            Tạo trình tạm ứng
          </Button>
        </div>
      </Form>
    </Modal>
  )
}

ProposalAdvanceCreateModal.propTypes = {}

export default inject(
  'proposalAdvanceStore',
  'selectPeopleStore',
  'proposalStore',
  'fileStore',
  'authenticationStore'
)(observer(ProposalAdvanceCreateModal))
